import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "../../../components/Box";
import { KTCard } from "../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../_metronic/helpers/components/KTCardBody";
import Content from "../../../_metronic/layout/components/content/Content";
import { ToolbarWrapper } from "../../../_metronic/layout/components/toolbar";
import { PageTitle } from "../../../_metronic/layout/core";
import { storeActivityIndicator, storeEbbeModalCallback } from "../../../redux/reducers/authSlice";
import { Helpers } from "../../../helpers/Helpers";
import { MailSablonuService } from "../../../services/MailSablonuService";
import { GetMailSablonuByIdResponse, MailSablonuDegisken, MailSablonuIcerikResponse, MailSablonuMesajResponse } from "../../../models/response/mailSablonu/MailSablonuResponses";
import DenemeMailiModal from "./Components/DenemeMailiModal";

/** Önizleme isteği yazmayı bıraktıktan bu kadar sonra gider. */
const ONIZLEME_BEKLEME_MS = 600;

const MailSablonuAdd = () => {
    const navigate = useNavigate();
    const pageName = "Ayarlar > Mail Şablonları"
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const [mailSablonuId, setMailSablonuId] = useState<string | undefined>(undefined);
    const [ad, setAd] = useState<string | undefined>(undefined);
    const [konu, setKonu] = useState<string>('');
    const [govde, setGovde] = useState<string>('');
    const [degiskenler, setDegiskenler] = useState<MailSablonuDegisken[]>([]);

    const [onizleme, setOnizleme] = useState<{ konu: string, govde: string } | undefined>(undefined);
    const [onizlemeHatasi, setOnizlemeHatasi] = useState<string | undefined>(undefined);
    const [denemeAcik, setDenemeAcik] = useState<boolean>(false);

    const konuRef = useRef<HTMLInputElement>(null);
    const govdeRef = useRef<HTMLTextAreaElement>(null);
    /** Değişken, en son hangi alanda yazılıyorsa oraya eklenir (varsayılan gövde). */
    const sonAlan = useRef<'konu' | 'govde'>('govde');
    const sonOnizlemeNo = useRef(0);

    useEffect(() => {
        getData();
    }, [])

    // Canlı önizleme: konu/gövde değişince 600 ms bekleyip sunucuda örnek verilerle doldurulur.
    useEffect(() => {
        if (!mailSablonuId)
            return;

        const istekNo = ++sonOnizlemeNo.current;

        const zamanlayici = setTimeout(async () => {
            try {
                var response: MailSablonuIcerikResponse = await MailSablonuService.onizle(konu, govde);

                if (istekNo != sonOnizlemeNo.current)
                    return;

                if (!response.isSuccess) {
                    setOnizlemeHatasi(response.message);
                    return;
                }

                setOnizlemeHatasi(undefined);
                setOnizleme(response.data);
            } catch (error: any) {
                if (istekNo == sonOnizlemeNo.current)
                    setOnizlemeHatasi(typeof error == 'string' ? error : 'Önizleme alınamadı.');
            }
        }, ONIZLEME_BEKLEME_MS);

        return () => clearTimeout(zamanlayici);
    }, [mailSablonuId, konu, govde])

    const getData = async () => {
        try {
            var id = searchParams.get("id");

            if (!id)
                return;

            dispatch(storeActivityIndicator(true));

            var response: GetMailSablonuByIdResponse = await MailSablonuService.getMailSablonuById(id);

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            setMailSablonuId(response.data.mailSablonuId);
            setAd(response.data.ad);
            setKonu(response.data.konu ?? '');
            setGovde(response.data.govde ?? '');
            setDegiskenler(response.data.degiskenler ?? []);
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            dispatch(storeActivityIndicator(false))
        }
    }

    /** {{Ad}} imlecin olduğu yere eklenir; seçili metin varsa onun yerine geçer. */
    const degiskenEkle = (_ad: string) => {
        const eklenecek = '{{' + _ad + '}}';
        const alan = sonAlan.current == 'konu' ? konuRef.current : govdeRef.current;
        const metin = sonAlan.current == 'konu' ? konu : govde;
        const setMetin = sonAlan.current == 'konu' ? setKonu : setGovde;

        const bas = alan?.selectionStart ?? metin.length;
        const son = alan?.selectionEnd ?? metin.length;

        setMetin(metin.substring(0, bas) + eklenecek + metin.substring(son));

        setTimeout(() => {
            if (alan) {
                alan.focus();
                alan.setSelectionRange(bas + eklenecek.length, bas + eklenecek.length);
            }
        }, 0);
    }

    const kaydet = async () => {
        try {
            if (!mailSablonuId)
                return;

            if (Helpers.isEmpty(konu))
                toast.error('Konu alanını doldurunuz!')
            else if (Helpers.isEmpty(govde))
                toast.error('Gövde alanını doldurunuz!')
            else {
                dispatch(storeActivityIndicator(true));

                var response: MailSablonuMesajResponse = await MailSablonuService.updateMailSablonu(mailSablonuId, konu, govde);

                if (!response.isSuccess) {
                    toast.error(response.message);
                    dispatch(storeActivityIndicator(false));
                    return;
                }

                toast.success(response.message)

                dispatch(storeActivityIndicator(false));
                navigate(Helpers.getGoBackUrl())
            }
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const varsayilanaDon = async () => {
        try {
            if (!mailSablonuId)
                return;

            dispatch(storeActivityIndicator(true));

            var response: MailSablonuIcerikResponse = await MailSablonuService.varsayilanaDon(mailSablonuId);

            if (!response.isSuccess) {
                toast.error(response.message);
                return;
            }

            setKonu(response.data.konu ?? '');
            setGovde(response.data.govde ?? '');
            toast.success('Varsayılan şablon forma yazıldı. Kalıcı olması için Kaydet\'e basınız.');
        } catch (error: any) {
            toast.error(error);
        }
        finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const varsayilanaDonOnay = () => {
        dispatch(storeEbbeModalCallback({
            title: 'Varsayılana Dön',
            body: 'Konu ve gövde sistemin varsayılan şablonuyla değiştirilecek. Kaydet\'e basmadıkça kayıtlı şablon değişmez. Devam etmek istiyor musunuz?',
            buttons: [
                {
                    text: 'Vazgeç',
                    btnClass: 'btn-warning',
                },
                {
                    text: 'Varsayılana Dön',
                    btnClass: 'btn-success',
                    onClick: varsayilanaDon
                }
            ]
        }))
    }

    const denemeGonder = async (_adres: string) => {
        try {
            dispatch(storeActivityIndicator(true));

            var response: MailSablonuMesajResponse = await MailSablonuService.denemeGonder(konu, govde, _adres);

            if (!response.isSuccess) {
                toast.error(response.message);
                return;
            }

            toast.success(response.message || 'Deneme maili gönderildi.');
            setDenemeAcik(false);
        } catch (error: any) {
            toast.error(error);
        }
        finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    return (
        <React.Fragment>
            <ToolbarWrapper filters={undefined} />

            <PageTitle breadcrumbs={[{
                title: pageName.split('>').slice(0, pageName.split('>').length - 1).join(' > ') + ' > ',
                isActive: false,
                path: ''
            }]}>{(pageName.split('>')[pageName.split('>').length - 1])}</PageTitle>

            <Box className="mb-10">
                <Content>
                    <KTCard className="ebbe-card">
                        <KTCardBody>
                            <Box className="mb-10" flexDirection="row" alignItems="center">
                                <Box justifyContent="center">
                                    <h4 className="text-black m-0">Şablon Bilgileri</h4>
                                </Box>
                                {
                                    ad && (
                                        <Box justifyContent="center" ml="12px">
                                            <span className="badge badge-light-primary fs-8 fw-semibold py-2 px-3">{ad}</span>
                                        </Box>
                                    )
                                }
                            </Box>

                            <div className="row">
                                <div className="col-xl-6">
                                    <label className="form-label required">Konu</label>
                                    <input
                                        ref={konuRef}
                                        value={konu}
                                        onChange={(e) => setKonu(e.target.value)}
                                        onFocus={() => sonAlan.current = 'konu'}
                                        type="text"
                                        style={{ border: '1px solid #F1F1F4' }}
                                        className="form-control form-control-solid"
                                    />

                                    <label className="form-label required mt-5">Gövde (HTML)</label>
                                    <textarea
                                        ref={govdeRef}
                                        rows={22}
                                        value={govde}
                                        onChange={(e) => setGovde(e.target.value)}
                                        onFocus={() => sonAlan.current = 'govde'}
                                        spellCheck={false}
                                        style={{ border: '1px solid #F1F1F4', width: '100%', resize: 'vertical', fontFamily: 'monospace', fontSize: 12 }}
                                        className="form-control form-control-solid"
                                    />
                                </div>

                                <div className="col-xl-6 mt-5 mt-xl-0">
                                    <label className="form-label">Önizleme (örnek verilerle)</label>
                                    <div className="form-control form-control-solid mb-3" style={{ border: '1px solid #F1F1F4' }}>
                                        <span className="text-gray-600 me-2">Konu:</span>{onizleme?.konu}
                                    </div>
                                    <iframe
                                        title="Mail önizleme"
                                        sandbox=""
                                        srcDoc={onizleme?.govde ?? ''}
                                        style={{ border: '1px solid #F1F1F4', borderRadius: 6, width: '100%', height: 480, background: '#fff' }}
                                    />
                                    {
                                        onizlemeHatasi && (
                                            <div className='text-danger' style={{ fontSize: 11.5, marginTop: 4 }}>
                                                {onizlemeHatasi}
                                            </div>
                                        )
                                    }
                                </div>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <h4 className="mb-5 text-black">Değişkenler</h4>

                            <div className="text-gray-600 fs-7 mb-5">
                                Değişkene tıklayınca imlecin olduğu yere eklenir. Koşullu blok: <code>{'{{#PoNo}} ... {{/PoNo}}'}</code> = PoNo boşsa bu kısım mailde gizlenir.
                            </div>

                            <div className="table-responsive">
                                <table className="table align-middle table-row-dashed ebbe-compact-table mb-0">
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th style={{ width: 1 }}>Değişken</th>
                                            <th>Açıklama</th>
                                            <th>Örnek</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            degiskenler.length == 0 && (
                                                <tr>
                                                    <td className="text-center" colSpan={3}>Kayıt bulunamadı.</td>
                                                </tr>
                                            )
                                        }
                                        {
                                            degiskenler.map((item) => (
                                                <tr key={item.ad}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-light-primary py-1 px-3"
                                                            title="İmlecin olduğu yere ekle"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => degiskenEkle(item.ad)}
                                                        >
                                                            {'{{' + item.ad + '}}'}
                                                        </button>
                                                    </td>
                                                    <td>{item.aciklama}</td>
                                                    <td>{item.ornek}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    <KTCard className="ebbe-card mt-10">
                        <KTCardBody>
                            <Box flexDirection="row" justifyContent="flex-end">
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light btn-active-light-primary" onClick={() => navigate(Helpers.getGoBackUrl())}>Geri</button>
                                </Box>
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light-danger" disabled={!mailSablonuId} onClick={varsayilanaDonOnay}>Varsayılana Dön</button>
                                </Box>
                                <Box mr="15px">
                                    <button className="btn btn-sm btn-light-primary" disabled={!mailSablonuId} onClick={() => setDenemeAcik(true)}>Deneme Maili Gönder</button>
                                </Box>
                                <Box>
                                    <button className="btn btn-primary btn-sm" disabled={!mailSablonuId} onClick={kaydet}>Kaydet</button>
                                </Box>
                            </Box>
                        </KTCardBody>
                    </KTCard>
                </Content>
            </Box>

            <DenemeMailiModal
                isShown={denemeAcik}
                onSend={denemeGonder}
                onClose={() => setDenemeAcik(false)}
            />
        </React.Fragment>
    )
}

export default MailSablonuAdd;
